<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { X } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n' // Disabled during MVP phase
import { useStore } from '@nanostores/vue'
import CheckboxGroup from './_CheckboxGroup.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { usePCSFilter } from '@/lib/usePCSFilter'
import { useBreakpoints, capitalizeFirstLetter } from '@/lib/utils'
import ProductList from './_ProductList.vue'
import BreadCrumb from './_BreadCrumb.vue'
import type { SearchFilters } from '@/services/pcsServiceTypes'
import { searchValue, setSearchValue } from '@/store/searchInput'
import { PCSPageNames, setPageName } from '@/store/pageName'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

const { t } = useI18n()

const SHOW_TRANSLATION_TOOL = false

const { isTabletLandscape, isLarge } = useBreakpoints()
const {
	getProductData,
	rawFilterData,
	clearAllFilters,
	updateProductData,
	currentProductList,
	isShowMore,
	currentPage,
	currentFilterOptions,
	totalResults,
	defaultMaxPrice,
	currentFilterAmount,
	isLoading,
	isInited,
	addFilterOptionsToUrl,
	setFilterPayloadFromUrl,
} = usePCSFilter()

const searchValueGlobal = useStore(searchValue)
const showRawTitle = ref(false)
const toggleTitle = () => {
	showRawTitle.value = !showRawTitle.value
}

const searchResultsCount = computed(() => {
	if (totalResults.value > 999) {
		return `999+ ${t('pcs_plural_result')}`
	}

	if (totalResults.value === 0 || totalResults.value === 1) {
		return `${totalResults.value} ${t('pcs_singular_result')}`
	}

	return `${totalResults.value} ${t('pcs_plural_result')}`
})
const isFilterOpen = ref(false)
const toggleFilterStatus = () => {
	isFilterOpen.value = !isFilterOpen.value
}
const closeFilter = () => {
	isFilterOpen.value = false
}
const setDefaultFilterOpenStatus = () => {
	if (isLarge.value) {
		isFilterOpen.value = true
	} else {
		isFilterOpen.value = false
	}
}

const handleApplyFilter = () => {
	updateProductData(searchKeyword.value)
	closeFilter()
}

const handleClearAllFilters = () => {
	clearAllFilters()
	updateProductData(searchKeyword.value)
}

const filterActiveClass = computed(() => {
	if (
		currentProductList.value.length === 0 &&
		currentFilterAmount.value === 0
	) {
		return 'bg-none text-n-300 cursor-not-allowed pointer-events-none'
	}
	if (currentFilterAmount.value > 0) {
		return 'bg-black text-white'
	}
	return ''
})

const currentFilterIconClass = computed(() => {
	if (
		currentProductList.value.length === 0 &&
		currentFilterAmount.value === 0
	) {
		return 'text-n-300'
	}
	if (currentFilterAmount.value > 0) {
		return 'text-white'
	}
	return 'text-n-950'
})

const displayedFilterAmount = computed(() => {
	if (currentFilterAmount.value > 99) {
		return '99+'
	}

	return currentFilterAmount.value
})

const handleLoadMore = async () => {
	currentPage.value += 1
	await getProductData(currentPage.value, searchKeyword.value)
}

type QueryParams = {
	categories?: string
	q?: string
}

const params = ref<QueryParams | null>(null)
const searchKeyword = computed(() => {
	if (params.value?.q) {
		return params.value.q
	}
	return '*'
})

const displayText = computed(() => {
	if (params.value) {
		if (params.value.categories) {
			return {
				text: params.value.categories,
				colorClass: 'text-black typography-h1-str',
			}
		}
		if (params.value.q) {
			return {
				text: params.value.q,
				colorClass: 'text-o-500 typography-h3-str',
			}
		}
	}
	return { text: '', colorClass: 'text-black typography-h1-str' }
})

const parseQueryParams = (): QueryParams => {
	const urlSearchParams = new URLSearchParams(window.location.search)
	return {
		categories: urlSearchParams.get('categories') || undefined,
		q: urlSearchParams.get('q') || undefined,
	}
}

const breadcrumbItems = computed(() => {
	return [
		{ text: t('pcs_breadcrumb_home'), link: '/shop' },
		...(params.value?.categories
			? [{ text: capitalizeFirstLetter(params.value.categories) }]
			: []),
	]
})

const checkValidPriceRange = (filter: SearchFilters) => {
	if (
		filter.price?.gte !== undefined &&
		filter.price?.lte !== undefined &&
		filter.price.gte > filter.price.lte
	) {
		return false
	}
	return true
}

watch(
	currentFilterOptions,
	(newVal, oldVal) => {
		const newOptionsString = JSON.stringify(newVal)
		const prevFilterOptions = JSON.stringify(oldVal)
		if (
			prevFilterOptions === newOptionsString ||
			!checkValidPriceRange(newVal) ||
			!isInited.value
		) {
			// Don't call api if filter options are the same or price range is invalid or not inited
			return
		}

		addFilterOptionsToUrl()

		// Only auto update product on desktop
		if (!isLarge.value) return
		updateProductData(searchKeyword.value)
	},
	{
		deep: true,
	},
)

watch(searchValueGlobal, async (newValue, oldValue) => {
	if (newValue !== oldValue) {
		await getProductData(currentPage.value, newValue)
	}
})

onMounted(async () => {
	setPageName(PCSPageNames.Search)
	setFilterPayloadFromUrl()

	params.value = parseQueryParams()
	if (params.value.q) {
		setSearchValue(params.value.q)
	}
	await getProductData(currentPage.value, searchKeyword.value)
	isInited.value = true
	setDefaultFilterOpenStatus()
})
</script>

<template>
	<div class="text-n-950">
		<div
			v-if="searchValueGlobal"
			class="mb-casual-friends mt-acquaintances flex flex-col gap-12px"
		>
			<span class="typography-s1-emp text-n-500">
				{{ t('pcs_you_searched_for') }}
			</span>
			<h1 :class="displayText.colorClass">“{{ searchValueGlobal }}”</h1>
		</div>
		<div
			v-else-if="params && params.categories"
			class="mb-casual-friends mt-acquaintances flex flex-col gap-close-friends"
		>
			<div class="flex">
				<BreadCrumb :items="breadcrumbItems" />
			</div>
			<h1 :class="displayText.colorClass">
				{{ capitalizeFirstLetter(params.categories) }}
			</h1>
		</div>

		<div
			class="flex flex-col items-center justify-between border-t border-n-100 mdp:flex-row mdp:border-b"
		>
			<div
				class="flex w-full items-center justify-between border-b border-n-100 py-close-friends mdp:w-auto mdp:border-none"
			>
				<div
					v-show="currentFilterAmount > 0"
					class="typography-s1-reg ml-16px hidden cursor-pointer text-o-600 underline underline-offset-2 lg:block"
					@click="clearAllFilters"
				>
					{{ t('pcs_filter_clear_all') }}
				</div>
				<div class="mx-16px hidden h-[17px] w-[1px] bg-n-200 mdp:block"></div>
				<div class="typography-b2-reg">{{ searchResultsCount }}</div>
				<div
					class="flex cursor-pointer items-center gap-best-friends rounded-lg p-best-friends mdp:order-first"
					:class="filterActiveClass"
					@click="toggleFilterStatus"
				>
					<BaseIcon
						name="filter-settings"
						class="h-16px w-16px"
						:class="currentFilterIconClass"
					/>
					<div class="typography-b1-str select-none !leading-[1rem]">
						{{ t('pcs_filter_button_name') }}
					</div>
					<div
						v-show="currentFilterAmount > 0"
						class="typography-c2-str grid h-16px min-w-[17px] place-content-center rounded-lg bg-white px-[3.5px] text-black"
					>
						{{ displayedFilterAmount }}
					</div>
				</div>
			</div>

			<div
				v-if="SHOW_TRANSLATION_TOOL"
				class="typography-b3-reg mt-good-friends flex w-full justify-between gap-16px bg-n-50 px-16px py-8px text-center mdp:mt-0 mdp:w-auto"
			>
				<span class="font-normal">{{ t('pcs_auto_translated_message') }}</span>
				<span
					class="cursor-pointer select-none font-medium text-o-600 underline hover:text-o-500 hover:no-underline"
					@click="toggleTitle"
				>
					{{
						showRawTitle
							? t('pcs_hide_original_language')
							: t('pcs_show_original_language')
					}}
				</span>
			</div>
		</div>

		<div class="mb-strangers mt-acquaintances flex gap-[52px]">
			<template v-if="isLarge">
				<CheckboxGroup v-show="isFilterOpen"></CheckboxGroup>
			</template>

			<div v-if="currentProductList.length">
				<ProductList
					:products="currentProductList"
					:isShowMore="isShowMore"
					:isFilterOpen="isFilterOpen"
					:showRawTitle="showRawTitle"
					:isLoading="isLoading"
					@loadMoreProducts="handleLoadMore"
				/>
			</div>

			<div
				v-if="!currentProductList.length && !isLoading"
				class="flex w-full flex-col items-center gap-32px py-strangers"
			>
				<div
					class="aspect-square w-[168px] bg-contain bg-center bg-no-repeat mdp:w-[224px] mdl:w-[300px]"
					style="
						background-image: url('https://b2c-cdn.buynship.com/websites/buyandship-2023/builds/215/e0c6598/RbamgyvczB9Y4vxVDHgysk2I2PIajuEE/assets/img/PCSNoResult.png');
					"
				></div>

				<!-- No filter result -->
				<div
					v-if="!!currentFilterAmount"
					class="flex flex-col items-center gap-16px"
				>
					<div class="typography-h5-str">{{ t('pcs_no_results_found') }}</div>
					<div class="typography-b2-reg">
						{{ t('pcs_advise_reset_filter') }}
					</div>
				</div>

				<!-- No search result -->
				<div v-else class="flex flex-col items-center">
					<div class="typography-h5-str mb-8px">
						{{ t('pcs_no_results_found_for') }}
					</div>
					<div class="typography-h5-str mb-16px">“{{ searchValueGlobal }}”</div>
					<div class="typography-b2-reg">
						{{ t('pcs_advise_check_keyword') }}
					</div>
				</div>
			</div>
		</div>

		<!-- Mobile and Tablet Portrait Bottom Sheet -->
		<Sheet v-if="!isTabletLandscape" v-model:open="isFilterOpen">
			<SheetContent class="rounded-t-lg border-none bg-white" side="bottom">
				<div class="flex h-[calc(100dvh-72px)] flex-col">
					<div class="relative mx-20px mt-good-friends text-center">
						<div class="typography-h5-str">
							{{ t('pcs_filter_button_name') }}
						</div>
						<X
							@click="closeFilter"
							class="absolute right-0 top-0 h-24px w-24px"
						/>
					</div>

					<div class="overflow-scroll px-20px py-16px">
						<CheckboxGroup></CheckboxGroup>
					</div>

					<div
						class="mx-24px mb-good-friends mt-close-friends flex justify-end gap-16px"
					>
						<BaseButton
							variant="secondary"
							size="medium"
							class="w-full mdp:w-[142px]"
							:isDisabled="currentFilterAmount === 0"
							@click="handleClearAllFilters"
						>
							{{ t('pcs_filter_clear_all') }}
						</BaseButton>

						<BaseButton
							variant="primary"
							size="medium"
							class="w-full !border-black !bg-black mdp:w-[142px]"
							@click="handleApplyFilter"
						>
							{{ t('pcs_filter_apply') }}
						</BaseButton>
					</div>
				</div>
			</SheetContent>
		</Sheet>

		<!-- Tablet Landscape Dialog -->
		<Dialog v-if="isTabletLandscape && !isLarge" v-model:open="isFilterOpen">
			<DialogContent
				class="w-[634px] max-w-none rounded-t-lg border-none bg-white"
			>
				<div class="flex h-[624px] max-h-[100dvh] flex-col">
					<div
						class="relative mx-24px mt-close-friends flex items-center justify-between"
					>
						<div class="typography-h4-str">
							{{ t('pcs_filter_button_name') }}
						</div>
						<X
							@click="closeFilter"
							class="absolute right-0 top-0 h-24px w-24px"
						/>
					</div>

					<div class="h-full overflow-scroll px-24px py-16px">
						<CheckboxGroup></CheckboxGroup>
					</div>

					<div
						class="mb-good-friends mt-close-friends flex justify-end gap-16px px-24px"
					>
						<BaseButton
							variant="secondary"
							size="medium"
							class="w-[142px]"
							:isDisabled="currentFilterAmount === 0"
							@click="handleClearAllFilters"
						>
							{{ t('pcs_filter_clear_all') }}
						</BaseButton>

						<BaseButton
							variant="primary"
							size="medium"
							class="w-[142px] !border-black !bg-black"
							@click="handleApplyFilter"
						>
							{{ t('pcs_filter_apply') }}
						</BaseButton>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	</div>
</template>
